/**
 * The collapsed "Snoozed" group at the bottom of both inbox pages: what is
 * deferred, when it returns, and "Wake now" — the correction path for a
 * snooze picked by mistake (the row moves to "Back from snooze").
 */
import { useState } from "react";
import { Link } from "react-router-dom";

import type { AdminReviewDoc } from "../../api/admin";
import { formatReturnDate, snoozeLabel, snoozedUntilDate } from "../../lib/snooze";

interface Props {
  rows: AdminReviewDoc[];
  linkTo: (doc: AdminReviewDoc) => string;
  title: (doc: AdminReviewDoc) => string;
  onWake: (doc: AdminReviewDoc) => void;
  wakingId?: string | null;
}

export function SnoozedSection({ rows, linkTo, title, onWake, wakingId }: Props) {
  const [open, setOpen] = useState(false);
  const [nowMs] = useState(() => Date.now());
  if (rows.length === 0) return null;
  return (
    <section className="panel panel--flush">
      <button
        type="button"
        className="queue-group-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden>{open ? "▾" : "▸"}</span>
        <span className="font-semibold">Snoozed</span>
        <span className="font-mono text-xs text-ink-low">{rows.length}</span>
        <span className="text-xs text-ink-low">– comes back on its own</span>
      </button>
      {open ? (
        <ul className="divide-y border-t border-rule">
          {rows.map((doc) => (
            <li key={doc.id} className="flex items-center gap-3 px-4 py-2">
              <Link to={linkTo(doc)} className="min-w-0 flex-1 truncate text-sm text-ink hover:text-accent">
                {title(doc)}
              </Link>
              {doc.agent_slug ? (
                <span className="font-mono text-xs text-ink-low">{doc.agent_slug}</span>
              ) : null}
              <span className="font-mono text-xs text-ink-medium" title={doc.snoozed_until ?? undefined}>
                returns {doc.snoozed_until ? formatReturnDate(snoozedUntilDate(doc.snoozed_until), nowMs) : "–"}
              </span>
              <span className="font-mono text-xs text-ink-low" title="Last snooze · times snoozed">
                {doc.snooze_days ? snoozeLabel(doc.snooze_days) : ""}
                {doc.snooze_count && doc.snooze_count > 1 ? ` · ${doc.snooze_count}×` : ""}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={wakingId === doc.id}
                onClick={() => onWake(doc)}
              >
                Wake now
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
